import React from 'react';
import { Table, Button, DatePicker, Input, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { FP211 } from '../../../services/p201Api';
import dayjs from 'dayjs';

interface Props {
  details: FP211[];
  onChange: (details: FP211[]) => void;
  disabled: boolean;
  onSelectRow: (index: number) => void;
  selectedRow: number;
}

const Tab2ColorDetails: React.FC<Props> = ({ details, onChange, disabled, onSelectRow, selectedRow }) => {
  const addRow = () => {
    const newRow: FP211 = {
      fp211_01: '',
      fp211_02: details.length + 1,
      fp211_11: '',
      fp211_12: null,
      fp211_121: '',
      fp211_13: null,
      fp211_131: '',
      fp211_14: null,
      fp211_17: '',
      fp211_90: 0,
      fp211_91: '',
      fp211_92: null,
      fp211_93: '',
      specs: [],
    };
    onChange([...details, newRow]);
  };

  const removeRow = (index: number) => {
    const next = details.filter((_, i) => i !== index).map((d, i) => ({ ...d, fp211_02: i + 1 }));
    onChange(next);
  };

  const updateField = (index: number, field: keyof FP211, value: any) => {
    const next = [...details];
    (next[index] as any)[field] = value;
    onChange(next);
  };

  const columns = [
    {
      title: '項數',
      dataIndex: 'fp211_02',
      width: 60,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_02' } as React.HTMLAttributes<HTMLElement>),
      render: (_: any, __: any, idx: number) => idx + 1,
    },
    {
      title: '色號',
      dataIndex: 'fp211_11',
      width: 120,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_11' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          disabled={disabled}
          maxLength={10}
          onChange={e => updateField(idx, 'fp211_11', e.target.value)}
        />
      ),
    },
    {
      title: '缸號',
      dataIndex: 'fp211_17',
      width: 100,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_17' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          disabled={disabled}
          maxLength={10}
          onChange={e => updateField(idx, 'fp211_17', e.target.value)}
        />
      ),
    },
    {
      title: '標準完成日',
      dataIndex: 'fp211_12',
      width: 150,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_12' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string | null, _: any, idx: number) => (
        <DatePicker
          value={val ? dayjs(val) : null}
          disabled={disabled}
          format="YYYY-MM-DD"
          onChange={d => updateField(idx, 'fp211_12', d ? d.format('YYYY-MM-DD') : null)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '標準完成時間',
      dataIndex: 'fp211_121',
      width: 120,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_121' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          disabled={disabled}
          maxLength={5}
          placeholder="HH:MM"
          onChange={e => updateField(idx, 'fp211_121', e.target.value)}
        />
      ),
    },
    {
      title: '希望完成日',
      dataIndex: 'fp211_13',
      width: 150,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_13' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string | null, _: any, idx: number) => (
        <DatePicker
          value={val ? dayjs(val) : null}
          disabled={disabled}
          format="YYYY-MM-DD"
          onChange={d => updateField(idx, 'fp211_13', d ? d.format('YYYY-MM-DD') : null)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '希望完成時間',
      dataIndex: 'fp211_131',
      width: 120,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_131' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string, _: any, idx: number) => (
        <Input
          value={val}
          disabled={disabled}
          maxLength={5}
          placeholder="HH:MM"
          onChange={e => updateField(idx, 'fp211_131', e.target.value)}
        />
      ),
    },
    {
      title: '實際完成日',
      dataIndex: 'fp211_14',
      width: 150,
      onHeaderCell: () => ({ 'data-contract-field': 'fp211_14' } as React.HTMLAttributes<HTMLElement>),
      render: (val: string | null, _: any, idx: number) => (
        <DatePicker
          value={val ? dayjs(val) : null}
          disabled={disabled}
          format="YYYY-MM-DD"
          onChange={d => updateField(idx, 'fp211_14', d ? d.format('YYYY-MM-DD') : null)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      width: 50,
      render: (_: any, __: any, idx: number) =>
        !disabled && (
          <Popconfirm title="確定刪除?" onConfirm={() => removeRow(idx)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!disabled && (
          <Button type="dashed" icon={<PlusOutlined />} onClick={addRow}>
            新增明細
          </Button>
        )}
        <span
          className="field-label"
          data-contract-field="fp210_19"
          style={{ color: '#666' }}
        >
          總項數: {details.length}
        </span>
      </div>
      <Table
        dataSource={details}
        columns={columns}
        rowKey={(_, idx) => String(idx)}
        pagination={false}
        size="small"
        scroll={{ x: 1200 }}
        onRow={(_, idx) => ({
          onClick: () => onSelectRow(idx ?? 0),
          style: { cursor: 'pointer', background: idx === selectedRow ? '#e6f7ff' : undefined },
        })}
      />
    </div>
  );
};

export default Tab2ColorDetails;
